import type { SortingState } from "@tanstack/react-table";
import {
  COHORT_LABELS,
  KIND_LABELS,
  type RosterActivityEvent,
  type RosterActivityEventFromApi
} from "@/features/roster-activity";

export type RosterResultsFilters = {
  firstName: string;
  lastName: string;
  facility: string;
};

export type RosterResultsSort =
  | "date-desc"
  | "date-asc"
  | "kind-asc"
  | "kind-desc"
  | "lastName-asc"
  | "lastName-desc"
  | "firstName-asc"
  | "firstName-desc"
  | "facility-asc"
  | "facility-desc"
  | "cohort-asc"
  | "cohort-desc";

export const DEFAULT_ROSTER_RESULTS_SORT: RosterResultsSort = "date-desc";

export function splitPersonName(personName: string): { firstName: string; lastName: string } {
  const trimmed = personName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export function enrichRosterActivityEvent(event: RosterActivityEventFromApi): RosterActivityEvent {
  const fallback = splitPersonName(event.personName);
  return {
    ...event,
    firstName: event.person?.firstName?.trim() || fallback.firstName,
    lastName: event.person?.lastName?.trim() || fallback.lastName,
    facilityName: event.person?.primaryFacility?.name?.trim() || ""
  };
}

function matchesFilter(value: string, query: string): boolean {
  if (!query.trim()) return true;
  return value.toLowerCase().includes(query.trim().toLowerCase());
}

export function filterRosterActivityEvents(
  events: RosterActivityEvent[],
  filters: RosterResultsFilters
): RosterActivityEvent[] {
  return events.filter(
    (event) =>
      matchesFilter(event.firstName ?? "", filters.firstName) &&
      matchesFilter(event.lastName ?? "", filters.lastName) &&
      matchesFilter(event.facilityName ?? "", filters.facility)
  );
}

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

export function sortRosterActivityEvents(
  events: RosterActivityEvent[],
  sort: RosterResultsSort
): RosterActivityEvent[] {
  const sorted = [...events];
  switch (sort) {
    case "date-asc":
      return sorted.sort(
        (left, right) =>
          left.occurredAt.localeCompare(right.occurredAt) ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          compareText(left.firstName ?? "", right.firstName ?? "")
      );
    case "kind-asc":
      return sorted.sort(
        (left, right) =>
          compareText(KIND_LABELS[left.kind], KIND_LABELS[right.kind]) ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "kind-desc":
      return sorted.sort(
        (left, right) =>
          compareText(KIND_LABELS[right.kind], KIND_LABELS[left.kind]) ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "lastName-asc":
      return sorted.sort(
        (left, right) =>
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          compareText(left.firstName ?? "", right.firstName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "lastName-desc":
      return sorted.sort(
        (left, right) =>
          compareText(right.lastName ?? "", left.lastName ?? "") ||
          compareText(left.firstName ?? "", right.firstName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "firstName-asc":
      return sorted.sort(
        (left, right) =>
          compareText(left.firstName ?? "", right.firstName ?? "") ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "firstName-desc":
      return sorted.sort(
        (left, right) =>
          compareText(right.firstName ?? "", left.firstName ?? "") ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "facility-asc":
      return sorted.sort(
        (left, right) =>
          compareText(left.facilityName ?? "", right.facilityName ?? "") ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "facility-desc":
      return sorted.sort(
        (left, right) =>
          compareText(right.facilityName ?? "", left.facilityName ?? "") ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "cohort-asc":
      return sorted.sort(
        (left, right) =>
          compareText(COHORT_LABELS[left.cohort], COHORT_LABELS[right.cohort]) ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "cohort-desc":
      return sorted.sort(
        (left, right) =>
          compareText(COHORT_LABELS[right.cohort], COHORT_LABELS[left.cohort]) ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          right.occurredAt.localeCompare(left.occurredAt)
      );
    case "date-desc":
    default:
      return sorted.sort(
        (left, right) =>
          right.occurredAt.localeCompare(left.occurredAt) ||
          compareText(left.lastName ?? "", right.lastName ?? "") ||
          compareText(left.firstName ?? "", right.firstName ?? "")
      );
  }
}

export function hasActiveRosterResultsFilters(filters: RosterResultsFilters): boolean {
  return Boolean(filters.firstName.trim() || filters.lastName.trim() || filters.facility.trim());
}

export function sortRosterEventsFromSortingState(
  events: RosterActivityEvent[],
  sorting: SortingState
): RosterActivityEvent[] {
  const primary = sorting[0];
  if (!primary) return sortRosterActivityEvents(events, DEFAULT_ROSTER_RESULTS_SORT);

  const { id, desc } = primary;
  if (id === "occurredAt") return sortRosterActivityEvents(events, desc ? "date-desc" : "date-asc");
  if (id === "kind") return sortRosterActivityEvents(events, desc ? "kind-desc" : "kind-asc");
  if (id === "lastName") return sortRosterActivityEvents(events, desc ? "lastName-desc" : "lastName-asc");
  if (id === "firstName") return sortRosterActivityEvents(events, desc ? "firstName-desc" : "firstName-asc");
  if (id === "facilityName") return sortRosterActivityEvents(events, desc ? "facility-desc" : "facility-asc");
  if (id === "cohort") return sortRosterActivityEvents(events, desc ? "cohort-desc" : "cohort-asc");
  return sortRosterActivityEvents(events, DEFAULT_ROSTER_RESULTS_SORT);
}
